import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import {
  FALLBACK_LOCALE,
  asDateFormat,
  asNumberFormat,
  asTimeFormat,
  formatDateTimeValue,
  formatMoneyValue,
  formatNumberValue,
  localeForCurrency,
  symbolForCurrency,
  type LocaleSnapshot,
} from "../lib/localeFormat";
import { useAuth } from "./AuthProvider";

interface OrgSettingsResponse {
  timezone: string;
  currency: string;
  currency_symbol: string;
  quote_approval_required: boolean;
  fiscal_year_start_month?: number;
  history_after_days?: number;
  date_format?: string;
  time_format?: string;
  number_format?: string;
}

interface PreferencesResponse {
  timezone: string;
  currency: string;
  currency_symbol: string;
  use_org_locale: boolean;
  saved: boolean;
  org_timezone?: string;
  org_currency?: string;
  org_currency_symbol?: string;
  date_format?: string;
  time_format?: string;
  number_format?: string;
}

interface LocaleContextValue extends LocaleSnapshot {
  refresh: () => void;
  formatMoney: (value: number | string | null | undefined, currency?: string | null, compact?: boolean) => string;
  formatNumber: (value: number | string | null | undefined, fractionDigits?: number) => string;
  formatDate: (value: string | Date | null | undefined) => string;
  formatDateTime: (value: string | Date | null | undefined) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export const LOCALE_CHANGED_EVENT = "sales:locale-changed";

export function notifyLocaleChanged(): void {
  window.dispatchEvent(new Event(LOCALE_CHANGED_EVENT));
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const orgId = session?.orgId;
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState<LocaleSnapshot>(FALLBACK_LOCALE);

  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener(LOCALE_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(LOCALE_CHANGED_EVENT, onChange);
  }, [refresh]);

  useEffect(() => {
    if (!orgId) {
      setSnapshot(FALLBACK_LOCALE);
      return;
    }
    const controller = new AbortController();
    void Promise.all([
      api.get<OrgSettingsResponse>(`/org/${encodeURIComponent(orgId)}/settings/locale`, controller.signal),
      api.get<PreferencesResponse>("/auth/preferences", controller.signal),
    ]).then(([org, pref]) => {
      if (controller.signal.aborted) return;
      const currency = pref.currency || org.currency;
      const dateFormat = asDateFormat(pref.date_format || org.date_format, currency);
      const timeFormat = asTimeFormat(pref.time_format || org.time_format, currency);
      const numberFormat = asNumberFormat(pref.number_format || org.number_format, currency);
      setSnapshot({
        timezone: pref.timezone || org.timezone,
        currency,
        currencySymbol: pref.currency_symbol || org.currency_symbol,
        locale: localeForCurrency(currency),
        dateFormat,
        timeFormat,
        numberFormat,
        quoteApprovalRequired: Boolean(org.quote_approval_required),
        orgTimezone: pref.org_timezone || org.timezone,
        orgCurrency: pref.org_currency || org.currency,
        orgCurrencySymbol: pref.org_currency_symbol || org.currency_symbol,
        useOrgLocale: pref.use_org_locale !== false,
        fiscalYearStartMonth: org.fiscal_year_start_month || 1,
        historyAfterDays: org.history_after_days ?? 1,
      });
    }).catch(() => {
      if (!controller.signal.aborted) setSnapshot(FALLBACK_LOCALE);
    });
    return () => controller.abort();
  }, [orgId, revision]);

  const value = useMemo<LocaleContextValue>(() => ({
    ...snapshot,
    refresh,
    formatMoney: (amount, currency, compact) => {
      const code = (currency || snapshot.currency).toUpperCase();
      const symbol = code === snapshot.currency.toUpperCase() ? snapshot.currencySymbol : symbolForCurrency(code);
      return formatMoneyValue(amount, {
        currency: code,
        locale: snapshot.locale,
        symbol,
        numberFormat: snapshot.numberFormat,
        compact,
      });
    },
    formatNumber: (amount, fractionDigits = 0) =>
      formatNumberValue(amount, { numberFormat: snapshot.numberFormat, fractionDigits }),
    formatDate: (when) => formatDateTimeValue(when, {
      timezone: snapshot.timezone,
      locale: snapshot.locale,
      dateOnly: true,
      dateFormat: snapshot.dateFormat,
    }),
    formatDateTime: (when) => formatDateTimeValue(when, {
      timezone: snapshot.timezone,
      locale: snapshot.locale,
      dateFormat: snapshot.dateFormat,
      timeFormat: snapshot.timeFormat,
    }),
  }), [refresh, snapshot]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext) ?? {
    ...FALLBACK_LOCALE,
    refresh: () => undefined,
    formatMoney: (amount, currency, compact) => {
      const code = (currency || FALLBACK_LOCALE.currency).toUpperCase();
      return formatMoneyValue(amount, {
        currency: code,
        locale: FALLBACK_LOCALE.locale,
        symbol: code === FALLBACK_LOCALE.currency ? FALLBACK_LOCALE.currencySymbol : symbolForCurrency(code),
        numberFormat: FALLBACK_LOCALE.numberFormat,
        compact,
      });
    },
    formatNumber: (amount, fractionDigits = 0) =>
      formatNumberValue(amount, { numberFormat: FALLBACK_LOCALE.numberFormat, fractionDigits }),
    formatDate: (when) => formatDateTimeValue(when, {
      timezone: FALLBACK_LOCALE.timezone,
      locale: FALLBACK_LOCALE.locale,
      dateOnly: true,
      dateFormat: FALLBACK_LOCALE.dateFormat,
    }),
    formatDateTime: (when) => formatDateTimeValue(when, {
      timezone: FALLBACK_LOCALE.timezone,
      locale: FALLBACK_LOCALE.locale,
      dateFormat: FALLBACK_LOCALE.dateFormat,
      timeFormat: FALLBACK_LOCALE.timeFormat,
    }),
  };
}
